import React, { useState } from "react";
import "../styles/fonts.css"; // Import the CSS for custom font classes

// A simple component for displaying code snippets with basic styling
function CodeSnippet({ code }) {
  return (
    <pre
      style={{
        backgroundColor: "#f4f4f4",
        padding: "16px",
        borderRadius: "4px",
        overflowX: "auto",
        textAlign: "left",
        fontSize: "12px"
      }}
    >
      <code>{code}</code>
    </pre>
  );
}

// Color palette
const colors = [
  { label: "White", hex: "#FFFFFF", rgb: "rgb(255, 255, 255)" },
  { label: "Blue", hex: "#005BBB", rgb: "rgb(0, 91, 187)" },
  { label: "Gray", hex: "#DCDCDC", rgb: "rgb(204, 204, 204)" },
  { label: "Black", hex: "#000000", rgb: "rgb(0, 0, 0)" },
];

// Fonts (make sure these are loaded via Google Fonts or other means)
const fonts = [
  {
    name: "Fredericka the Great",
    fontFamily: "'Fredericka the Great', serif",
    usage: "Used for large, decorative headings or hero text.",
    size: "64px", // about 2.5rem
  },
  {
    name: "Fredoka",
    fontFamily: "'Fredoka', sans-serif",
    usage: "Used for subheadings or call-to-action text.",
    size: "32px", // about 1.5rem
  },
  {
    name: "Urbanist",
    fontFamily: "'Urbanist', sans-serif",
    usage: "Used for body text, forms, and general UI elements.",
    size: "16px", // about 1rem
  },
];

// A sub-component for each color swatch + copy buttons
function ColorCard({ label, hex, rgb }) {
  // Track button text for Hex and RGB
  const [hexButtonText, setHexButtonText] = useState("Copy Hex");
  const [rgbButtonText, setRgbButtonText] = useState("Copy RGB");

  // Copies text to the clipboard, then updates button text
  const copyToClipboard = (text, isHex = true) => {
    navigator.clipboard.writeText(text).then(
      () => {
        if (isHex) {
          setHexButtonText(`Copied!`);
          setTimeout(() => setHexButtonText("Copy Hex"), 2000);
        } else {
          setRgbButtonText(`Copied!`);
          setTimeout(() => setRgbButtonText("Copy RGB"), 2000);
        }
      },
      () => {
        // Handle any copy error here if needed
      }
    );
  };

  return (
    <div style={styles.colorCard}>
      <div
        style={{
          ...styles.colorSwatch,
          backgroundColor: hex,
        }}
      />
      <p style={styles.colorLabel}>
        {label} <span style={styles.hexValue}>({hex})</span>
      </p>
      <div style={styles.buttonGroup}>
        <button style={styles.copyButton}  onClick={() => copyToClipboard(hex, true)}>
          {hexButtonText}
        </button>
        <button style={styles.copyButton} onClick={() => copyToClipboard(rgb, false)}>
          {rgbButtonText}
        </button>
      </div>
    </div>
  );
}

// Modal component example
function Modal({ onClose }) {
  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h3 className="fredoka-custom">Modal Title</h3>
        <p>This is an example modal. It can contain any content such as forms or messages.</p>
        <button style={styles.copyButton} onClick={onClose}>
          Close Modal
        </button>
      </div>
    </div>
  );
}

function StyleGuide() {
  const [modalOpen, setModalOpen] = useState(false);

  // Code snippet for embedding Google Fonts in the <head> of your HTML
  const googleFontsEmbed = `<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fredericka+the+Great&family=Fredoka:wght@300..700&family=Urbanist:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet">`;

  // Code snippet for the fonts.css file that provides custom CSS classes
  const fontsCss = `/* fonts.css */

/* Fredericka the Great */
.fredericka-the-great-regular {
  font-family: "Fredericka the Great", serif;
  font-weight: 400;
  font-style: normal;
}

/* Fredoka */
/* Rename "fredoka-custom" if desired to be more descriptive */
.fredoka-custom {
  font-family: "Fredoka", sans-serif;
  font-optical-sizing: auto;
  font-weight: 400; /* Adjust weight between 300 and 700 as needed */
  font-style: normal;
  font-variation-settings: "wdth" 100;
}`;

  // Code snippet for usage in a React component with fonts
  const fontsUsageExample = `import "../styles/fonts.css";

function Example() {
  return (
    <div>
      <h1 className="fredericka-the-great-regular">Fredericka the Great Heading</h1>
      <p className="fredoka-custom" style={{ fontWeight: 500 }}>
        This is sample text using Fredoka at weight 500.
      </p>
    </div>
  );
}

export default Example;`;

  // Code snippet for Layout and Navigation Templates
  const layoutSnippet = `/* layout.css */
.container {
  display: grid;
  grid-template-areas:
    "header header"
    "sidebar main";
  grid-template-columns: 250px 1fr;
  grid-template-rows: auto 1fr;
  min-height: 100vh;
}
.header {
  grid-area: header;
  background: #005BBB;
  color: #FFF;
  padding: 16px;
}
.sidebar {
  grid-area: sidebar;
  background: #DCDCDC;
  padding: 16px;
}
.main {
  grid-area: main;
  padding: 16px;
}`;

  // Code snippet for Color Palette usage via CSS variables
  const colorPaletteSnippet = `/* colors.css */
:root {
  --color-white: #FFFFFF;
  --color-blue: #005BBB;
  --color-gray: #DCDCDC;
  --color-black: #000000;
}

body {
  background-color: var(--color-white);
  color: var(--color-black);
}`;

  // Code snippet for Popup/Modal guidelines
  const modalSnippet = `/* modal.css */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  background: #FFF;
  max-width: 600px;
  width: 90%;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}`;

  // Code snippet for Feedback (Success & Error messages)
  const feedbackSnippet = `/* feedback.css */
.success-message {
  background-color: #d4edda;
  color: #155724;
  padding: 16px;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  margin: 16px 0;
}
.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 16px;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin: 16px 0;
}`;

  // Code snippet for Methods for Inline Help (Tooltip example)
  const inlineHelpSnippet = `/* tooltip.css */
.tooltip {
  position: relative;
  display: inline-block;
  cursor: pointer;
}
.tooltip .tooltiptext {
  visibility: hidden;
  width: 140px;
  background-color: #555;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 8px 0;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -70px;
  opacity: 0;
  transition: opacity 0.3s;
}
.tooltip:hover .tooltiptext {
  visibility: visible;
  opacity: 1;
}`;

  return (
    <div style={styles.container}>
      <h1 style={{ ...styles.title, fontSize: "64px" }} className="fredericka-the-great-regular">
  Style Guide
</h1>

      {/* Colors Section */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Colors:</h2>
      <div style={styles.colorsContainer}>
        {colors.map((color) => (
          <ColorCard
            key={color.hex}
            label={color.label}
            hex={color.hex}
            rgb={color.rgb}
          />
        ))}
      </div>

      {/* Fonts Section */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Fonts and Font Sizes (Where to Use Them):</h2>
      <div style={styles.fontsContainer}>
        {fonts.map((font) => (
          <div key={font.name} style={styles.fontCardBox}>
            <div style={styles.fontCard}>
              <p
                style={{
                  fontFamily: font.fontFamily,
                  fontSize: font.size,
                  margin: "0 0 4px 0",
                }}
              >
                {font.name}
              </p>
              <br />
              <p style={{ margin: 0, fontSize: "20px" }}>
                <strong>Usage:</strong> {font.usage}
              </p>
              <br />
              <p style={{ margin: 0, fontSize: "20px" }}>
                <strong>Recommended Size:</strong> {font.size}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Layout and Navigation Templates */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Layout &amp; Navigation Templates:</h2>
      <p style={styles.textBlock}>
        <strong>General Layout:</strong> Use a consistent header (or top navbar)
        and sidebar across all pages. Main content goes in the center/right area,
        ensuring a clean and predictable user experience. Keep primary
        navigation in the top or left sidebar, and secondary actions on their respective pages.
      </p>

      {/* Popups/Modals Guidelines */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Size/Position/Interaction Guidelines for Popups/Modals:</h2>
      <p style={styles.textBlock}>
        <strong>Size &amp; Position:</strong> Modals should be centered
        horizontally and vertically on larger screens, with a max width of ~600px
        and appropriate padding. On mobile, modals can take up most of the screen
        width.
        <br />
        <strong>Interaction:</strong> Clicking outside the modal or on a close
        icon/button should dismiss the modal. The modal should trap focus for
        accessibility.
      </p>

      {/* Treatment of Feedback */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Treatment of Feedback:</h2>
      <p style={styles.textBlock}>
        <strong>Success Messages:</strong> Display a brief green banner or toast
        message. For inline form feedback, use green text or a check icon near
        the relevant field.
        <br />
        <strong>Error Messages:</strong> Use a red banner, toast, or inline text
        indicating what went wrong and how to fix it. For form errors, place the
        error text near the invalid field and highlight the border in red.
      </p>

      {/* Methods for Inline Help */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Methods for Inline Help:</h2>
      <p style={styles.textBlock}>
        Provide inline help via tooltips, small "?" icons, or short descriptive
        text under form fields. Keep the help concise but clear, and only show
        it when needed (e.g., on hover or focus).
      </p>

      {/* EXAMPLES */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Examples</h2>
      <div style={styles.examplesContainer}>
        {/* Modal Example */}
        <h3 style={styles.exampleHeading} className="fredoka-custom">Modal Example</h3>
        <p style={styles.exampleText}>Click the button below to open a modal.</p>
        <button style={styles.copyButton} onClick={() => setModalOpen(true)}>
          Open Modal
        </button>
        {modalOpen && <Modal onClose={() => setModalOpen(false)} />}
        
        {/* Feedback Example */}
        <h3 style={styles.exampleHeading} className="fredoka-custom">Feedback Examples</h3>
        <p style={styles.exampleText}>Below are examples of success and error feedback messages:</p>
        <div style={styles.feedbackContainer}>
          <div style={styles.successMessage}>
            <strong>Success:</strong> Your action was completed successfully.
          </div>
          <div style={styles.errorMessage}>
            <strong>Error:</strong> Something went wrong. Please try again.
          </div>
        </div>
        
        {/* Inline Help Example */}
        <h3 style={styles.exampleHeading} className="fredoka-custom">Inline Help Example</h3>
        <p style={styles.exampleText}>Hover over the question mark to see a tooltip:</p>
        <div className="tooltip" style={{ display: "inline-block", position: "relative" }}>
          <span style={{ background: "#ccc", borderRadius: "50%", padding: "5px" }}>?</span>
          <span className="tooltiptext" style={{
            visibility: "hidden",
            width: "140px",
            backgroundColor: "#555",
            color: "#fff",
            textAlign: "center",
            borderRadius: "6px",
            padding: "8px 0",
            position: "absolute",
            zIndex: 1,
            bottom: "125%",
            left: "50%",
            marginLeft: "-70px",
            opacity: 0,
            transition: "opacity 0.3s"
          }}>
            This is a tooltip with inline help.
          </span>
        </div>
      </div>

      {/* Code Snippets Section */}
      <h2 style={styles.sectionHeading} className="fredoka-custom">Code Snippets</h2>
      <div style={{ textAlign: "left", margin: "0 auto", maxWidth: "600px" }}>
        <h4>1. Google Fonts Embed Code (Place in your HTML &lt;head&gt;)</h4>
        <CodeSnippet code={googleFontsEmbed} />
        <h4>2. CSS for Font Classes (fonts.css)</h4>
        <CodeSnippet code={fontsCss} />
        <h4>3. Example Usage in a React Component (fonts usage)</h4>
        <CodeSnippet code={fontsUsageExample} />
        <h4>4. Layout and Navigation (layout.css)</h4>
        <CodeSnippet code={layoutSnippet} />
        <h4>5. Color Palette (colors.css)</h4>
        <CodeSnippet code={colorPaletteSnippet} />
        <h4>6. Popup/Modal Guidelines (modal.css)</h4>
        <CodeSnippet code={modalSnippet} />
        <h4>7. Feedback Styles (feedback.css)</h4>
        <CodeSnippet code={feedbackSnippet} />
        <h4>8. Inline Help (Tooltip example - tooltip.css)</h4>
        <CodeSnippet code={inlineHelpSnippet} />
      </div>
    </div>
  );
}

/* Inline styles in px */
const styles = {
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "32px 16px", // Reduced side padding for mobile
    fontFamily: "'Urbanist', sans-serif",
    textAlign: "center",
    boxSizing: "border-box",
    width: "100%",
    overflowX: "hidden",
    "@media (max-width: 768px)": {
      padding: "16px",
    }
  },
  title: {
    fontSize: "32px",
    marginBottom: "32px",
    "@media (max-width: 768px)": {
      fontSize: "24px",
      marginBottom: "24px",
    },
    "@media (max-width: 480px)": {
      fontSize: "20px",
      marginBottom: "20px",
    }
  },
  sectionHeading: {
    fontSize: "24px",
    margin: "48px 0 24px 0",
    textAlign: "center",
    "@media (max-width: 768px)": {
      fontSize: "20px",
      margin: "32px 0 16px 0",
    },
    "@media (max-width: 480px)": {
      fontSize: "18px",
      margin: "24px 0 12px 0",
    }
  },
  colorsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "32px",
    flexWrap: "wrap",
    "@media (max-width: 768px)": {
      gap: "16px",
    }
  },
  colorCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "200px",
    marginBottom: "16px",
    fontFamily: "'Urbanist', sans-serif",
    "@media (max-width: 768px)": {
      width: "160px",
    },
    "@media (max-width: 480px)": {
      width: "140px",
    }
  },
  colorSwatch: {
    width: "160px",
    height: "160px",
    border: "1px solid #333",
    marginBottom: "8px",
    "@media (max-width: 768px)": {
      width: "120px",
      height: "120px",
    },
    "@media (max-width: 480px)": {
      width: "100px",
      height: "100px",
    }
  },
  colorLabel: {
    fontSize: "16px",
    margin: "8px 0",
    "@media (max-width: 480px)": {
      fontSize: "14px",
    }
  },
  hexValue: {
    fontStyle: "italic",
    fontSize: "14px",
    marginLeft: "4px",
    "@media (max-width: 480px)": {
      fontSize: "12px",
    }
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "4px",
    width: "100%",
    "@media (max-width: 768px)": {
      gap: "4px",
    }
  },
  copyButton: {
    
    backgroundColor: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 12px",
    cursor: "pointer",
    fontFamily: "'Urbanist', sans-serif",
    width: "100%",
    "@media (max-width: 480px)": {
      padding: "6px 10px",
      fontSize: "14px",
    }
  },
  fontsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  fontCardBox: {
    backgroundColor: "#e0e0e0",
    padding: "16px",
    borderRadius: "8px",
    marginBottom: "16px",
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 768px)": {
      padding: "12px",
    }
  },
  fontCard: {
    textAlign: "center",
    "@media (max-width: 768px)": {
      fontSize: "14px",
    }
  },
  textBlock: {
    textAlign: "left",
    margin: "0 auto 48px auto",
    maxWidth: "600px",
    lineHeight: 1.5,
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 768px)": {
      margin: "0 auto 32px auto",
      fontSize: "14px",
    }
  },
  examplesContainer: {
    textAlign: "center",
    margin: "0 auto 48px auto",
    maxWidth: "600px",
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 768px)": {
      margin: "0 auto 32px auto",
    }
  },
  exampleHeading: {
    fontSize: "20px",
    margin: "16px 0 8px 0",
    "@media (max-width: 768px)": {
      fontSize: "18px",
    }
  },
  exampleText: {
    fontSize: "16px",
    marginBottom: "16px",
    "@media (max-width: 768px)": {
      fontSize: "14px",
      marginBottom: "12px",
    }
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#FFF",
    maxWidth: "600px",
    width: "90%",
    padding: "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    textAlign: "center",
    "@media (max-width: 768px)": {
      padding: "16px",
      width: "95%",
    }
  },
  feedbackContainer: {
    maxWidth: "600px",
    margin: "0 auto 48px auto",
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 768px)": {
      margin: "0 auto 32px auto",
    }
  },
  successMessage: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "16px",
    border: "1px solid #c3e6cb",
    borderRadius: "4px",
    marginBottom: "16px",
    textAlign: "center",
    "@media (max-width: 768px)": {
      padding: "12px",
      fontSize: "14px",
    }
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "16px",
    border: "1px solid #f5c6cb",
    borderRadius: "4px",
    textAlign: "center",
    "@media (max-width: 768px)": {
      padding: "12px",
      fontSize: "14px",
    }
  },
};

// Create a function to apply media query styles
const applyResponsiveStyles = (baseStyles) => {
  const finalStyles = { ...baseStyles };
  delete finalStyles['@media'];
  
  // Get all media queries
  const mediaQueries = Object.keys(baseStyles).filter(key => key.startsWith('@media'));
  
  // Apply media queries using matchMedia
  mediaQueries.forEach(query => {
    const mediaQuery = window.matchMedia(query.replace('@media', '').trim());
    const handleChange = (e) => {
      if (e.matches) {
        Object.assign(finalStyles, baseStyles[query]);
      }
    };
    mediaQuery.addListener(handleChange);
    handleChange(mediaQuery);
  });
  
  return finalStyles;
};

// Update the component to use the responsive styles
Object.keys(styles).forEach(key => {
  styles[key] = applyResponsiveStyles(styles[key]);
});

export default StyleGuide;
